import { Hierarchy } from "@models/hierarchy";
import { useQueries, useQuery } from "@tanstack/react-query";
import { axiosInstance } from "../axios-instance";
import { flattenHierarchy, transformHierarchy } from "../services";
import { compareEquipmentNames } from "../services/natural-compare";

const keys = {
  hierarchy: () => ["hierarchy"] as const,
  rootEquipment: (site: string) => [site, "root-equipment"] as const,
  rootEquipmentAttributesBulk: (site: string, attributes: string[]) =>
    [
      site,
      "root-equipment",
      "attributes",
      "bulk",
      [...attributes].sort(),
    ] as const,
};

const urls = {
  hierarchy: () => `/sites/root-equipment/hierarchy`,
  rootEquipment: (site: string) => `/sites/${site}/root-equipments`,
  rootEquipmentAttributesBulk: (site: string) =>
    `/customers/${encodeURIComponent(site)}/root-equipments/attributes/bulk`,
};

export function useGetHierarchyApi(enabled: boolean = true) {
  return useQuery<Hierarchy>({
    queryKey: keys.hierarchy(),
    queryFn: async (): Promise<Hierarchy> => {
      const url = urls.hierarchy();
      return (await axiosInstance.get<Hierarchy>(url)).data;
    },
    staleTime: Infinity,
    enabled,
  });
}

export function useGetEquipmentHierarchy(enabled: boolean = true) {
  const { data, ...rest } = useGetHierarchyApi(enabled);
  return { data: data ? transformHierarchy(data) : undefined, ...rest };
}

export function useGetFlatHierarchy(enabled: boolean = true) {
  const { data, ...rest } = useGetHierarchyApi(enabled);
  return { data: data ? flattenHierarchy(data) : undefined, ...rest };
}

async function fetchRootEquipment(site: string) {
  const url = urls.rootEquipment(site);
  return (await axiosInstance.get<string[]>(url)).data.sort(
    compareEquipmentNames
  );
}

export function useRootEquipmentApi(site?: string) {
  return useQuery<string[]>({
    queryKey: keys.rootEquipment(site!),
    queryFn: () => fetchRootEquipment(site!),
    staleTime: Infinity,
    enabled: !!site,
  });
}

export function useRootEquipmentAttributesBulkApi(
  site: string | undefined,
  attributes: string[],
  enabled: boolean = true
) {
  return useQuery<Record<string, Record<string, string | null>>>({
    queryKey: keys.rootEquipmentAttributesBulk(site ?? "", attributes),
    queryFn: async () => {
      const url = urls.rootEquipmentAttributesBulk(site!);
      return (
        await axiosInstance.post<Record<string, Record<string, string | null>>>(
          url,
          { attributes }
        )
      ).data;
    },
    enabled: enabled && !!site,
    retry: false,
  });
}

export function useMultiSiteRootEquipmentApi(
  sites: string[],
  enabled: boolean = true
) {
  return useQueries({
    queries: sites.map((site) => ({
      queryKey: keys.rootEquipment(site),
      queryFn: () => fetchRootEquipment(site),
      staleTime: Infinity,
      enabled: !!site && enabled,
      retry: false,
      select: (data: string[]) => ({ site, rootEquipment: data }),
    })),
    combine: (results) => ({
      data: results.map((result) => result.data),
      isLoading: results.some((result) => result.isLoading),
      isError: results.some((result) => result.isError),
      failedSites: results
        .map((result, index) => (result.isError ? sites[index] : undefined))
        .filter((site): site is string => !!site),
    }),
  });
}
